import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Car,
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  Fuel,
  Gauge,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { extractErrorMessage } from '@/lib/api-client';
import { useDeleteVehicle, useVehiclesList } from './api';
import { VehicleFormDialog } from './vehicle-form-dialog';
import {
  FUEL_TYPE_LABELS,
  VEHICLE_CATEGORY_LABELS,
  type Vehicle,
  type VehicleCategory,
} from './types';

export function VehiclesPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<VehicleCategory | 'all'>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Vehicle | null>(null);

  const debouncedSearch = useDebouncedValue(search, 300);

  const query = useVehiclesList({
    page,
    limit: 20,
    q: debouncedSearch || undefined,
    category: category === 'all' ? undefined : category,
    sort: '-createdAt',
  });

  const deleteMutation = useDeleteVehicle();

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (vehicle: Vehicle) => {
    setEditing(vehicle);
    setFormOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success(`${deleteTarget.plate} removido`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const total = query.data?.meta.total ?? 0;
  const items = query.data?.data ?? [];

  return (
    <div className="page">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <Car className="size-3.5" />
            Frota
          </div>
          <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">Veículos</h1>
          <p className="text-sm text-muted-foreground">
            Cadastro completo da sua frota. Placa, combustível, odômetro e documentação.
          </p>
        </div>
        <Button onClick={openCreate} size="lg">
          <Plus className="size-4" />
          Novo veículo
        </Button>
      </header>

      {/* Filtros */}
      <Card>
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Buscar por placa, marca ou modelo…"
              className="pl-9"
            />
          </div>
          <Select
            value={category}
            onValueChange={(v) => {
              setCategory(v as VehicleCategory | 'all');
              setPage(1);
            }}
          >
            <SelectTrigger className="md:w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as categorias</SelectItem>
              {Object.entries(VEHICLE_CATEGORY_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Placa</th>
                  <th className="px-4 py-3 font-medium">Modelo</th>
                  <th className="px-4 py-3 font-medium">Categoria</th>
                  <th className="px-4 py-3 font-medium">Combustível</th>
                  <th className="px-4 py-3 font-medium text-right">Odômetro</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="w-10 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {query.isLoading &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <td key={j} className="px-4 py-3">
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))}

                {!query.isLoading && items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <div className="mx-auto flex max-w-sm flex-col items-center gap-3">
                        <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Car className="size-6" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Nenhum veículo ainda</p>
                          <p className="text-xs text-muted-foreground">
                            {debouncedSearch || category !== 'all'
                              ? 'Nada encontrado com os filtros atuais. Ajuste a busca ou limpe os filtros.'
                              : 'Comece cadastrando o primeiro veículo da sua frota.'}
                          </p>
                        </div>
                        {!debouncedSearch && category === 'all' && (
                          <Button onClick={openCreate} size="sm">
                            <Plus className="size-4" />
                            Cadastrar primeiro veículo
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}

                {items.map((v) => (
                  <tr
                    key={v.id}
                    className="group transition-colors hover:bg-muted/30"
                    onClick={() => navigate(`/app/vehicles/${v.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-semibold tracking-wide text-primary underline-offset-2 group-hover:underline">
                        {v.plate}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col leading-tight">
                        <span className="font-medium">
                          {v.brand || v.model ? `${v.brand ?? ''} ${v.model ?? ''}`.trim() : '—'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {v.year ? `Ano ${v.year}` : 'Ano —'}
                          {v.color ? ` · ${v.color}` : ''}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="secondary">{VEHICLE_CATEGORY_LABELS[v.category]}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <Fuel className="size-3.5 text-muted-foreground" />
                        {FUEL_TYPE_LABELS[v.fuelType]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center gap-1.5 font-mono text-xs tabular-nums text-muted-foreground">
                        <Gauge className="size-3.5" />
                        {(v.odometerMeters / 1000).toLocaleString('pt-BR')} km
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {v.active ? (
                        <Badge variant="success">Ativo</Badge>
                      ) : (
                        <Badge variant="outline">Inativo</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="size-8">
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/app/vehicles/${v.id}`); }}>
                            <Eye className="size-4" />
                            Ver detalhes
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEdit(v); }}>
                            <Pencil className="size-4" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(v); }}
                            className="text-destructive"
                          >
                            <Trash2 className="size-4" />
                            Remover
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > 20 && (
            <div className="flex items-center justify-between border-t border-border px-4 py-3 text-xs text-muted-foreground">
              <span>
                Mostrando {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} de {total}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * 20 >= total}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <VehicleFormDialog open={formOpen} onOpenChange={setFormOpen} vehicle={editing} />

      {/* Dialog de confirmação de delete */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remover veículo</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover <strong>{deleteTarget?.plate}</strong>? Os dados
              ficam preservados no histórico, mas o veículo some das listagens.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              loading={deleteMutation.isPending}
            >
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
